import MobileNavMenu from '@/components/header/nav-menu/MobileNavMenu';
import RegularNavMenu from '@/components/header/nav-menu/RegularNavMenu';
import { usePurchaseNavGuard } from '@/hooks/usePurchaseNavGuard';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import type { NavigationItem } from '@/types/navigation';
import { breakpoints } from '@/utils/breakpoints';
import { Icons } from '@packages/webshop-design-system';
import type { FC, ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

const Header: FC = () => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  // Leaving an in-progress purchase via "Purchase" must confirm a real BE
  // cancellation first (instead of silently dropping local state); the guard
  // owns that click behaviour and the confirmation modal.
  const { handlePurchaseClick, cancelModal } = usePurchaseNavGuard();
  const tenantConfiguration = useTenantConfiguration();

  const menuItems: Array<NavigationItem> = [
    {
      path: '/purchase',
      name: <TransText i18nKey="purchase" />,
      testId: 'purchase',
      onClick: handlePurchaseClick,
    },
    {
      path: '/manage-booking',
      name: <TransText i18nKey="manageBooking" />,
      testId: 'manage-booking',
    },
  ];

  const userMenuItems: Array<[TransTextKeys, string, ReactNode]> = [
    ['tickets', '/account/tickets', <Icons.ticket height={20} width={20} />],
    ...(tenantConfiguration?.showAccountMenuTravelPass
      ? ([
          [
            'travelPasses',
            '/account/travel-passes',
            <Icons.travelPass height={20} width={20} />,
          ],
        ] as Array<[TransTextKeys, string, ReactNode]>)
      : []),
    ...(tenantConfiguration?.showAccountMenuVouchers
      ? ([
          [
            'vouchers',
            '/account/vouchers',
            <Icons.wallet height={20} width={20} />,
          ],
        ] as Array<[TransTextKeys, string, ReactNode]>)
      : []),
    [
      'accountDetails',
      '/account/details',
      <Icons.user height={20} width={20} />,
    ],
    ['logOut', 'logout', <Icons.logOut height={20} width={20} />],
  ];

  const NavMenu = isLaptopOrBigger ? RegularNavMenu : MobileNavMenu;

  return (
    <header className="flex w-full flex-shrink-0 items-center border-b border-neutral-light bg-white text-dark">
      <div className="mx-auto w-full max-w-laptop px-4 py-2">
        <NavMenu menuItems={menuItems} userMenuItems={userMenuItems} />
      </div>
      {cancelModal}
    </header>
  );
};

export default Header;
